import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { CacheBusterService } from './core/services/cache-buster.service';
import { CustomToastService } from './core/services/custom-toast.service';
import { MasterService } from './core/services/master.service';
import { environment } from './environments/environment';
import { Apps } from './shared/enums/enums';
import { Signout } from './shared/models/shared.models';
import { SharedService } from './shared/services/shared.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 })),
      ]),
    ]),
  ],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Brijjworks';
  successMsg: string = '';
  clientErrorMsg: string = ''; // New property for client errors
  serverErrorMsg: string = ''; // New property for server errors
  processMsg: string = '';
  showSuccess: boolean = false;
  showClientError: boolean = false; // New flag for client errors
  showServerError: boolean = false; // New flag for server errors
  showProcessing: boolean = false;
  userDetails: any;

  private successTimeout: any;
  private clientErrorTimeout: any; // New timeout for client errors
  private serverErrorTimeout: any; // New timeout for server errors
  private subscriptions: Subscription[] = [];
  private currentUrl: string = '';

  constructor(
    private toast: CustomToastService,
    private router: Router,
    private cacheBusterService: CacheBusterService,
    private readonly sharedService: SharedService,
    private masterService: MasterService,
    private cookieService: CookieService,
  ) {}

  ngOnInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
      }
    });
    this.cacheBusterService.init();
    this.setupToastListeners();
    this.handleBrowserRefresh();

    this.userDetails = this.masterService.getUserDetailFromStorage();
    setInterval(() => {
      const cookies = document.cookie.split(';').map((c) => c.trim());
      const logoutCookie = cookies.find((c) => c.startsWith('logoutEvent='));
      if (logoutCookie) {
        this.logout();
        this.cookieService.delete('logoutEvent', '/');
        this.cookieService.delete('logoutEvent', '/', `.${environment.domainName}`);
      }
    }, 3000);

    if (!environment.production) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.head.appendChild(meta);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
    }
    if (this.clientErrorTimeout) {
      clearTimeout(this.clientErrorTimeout);
    }
    if (this.serverErrorTimeout) {
      clearTimeout(this.serverErrorTimeout);
    }
  }
  private setupToastListeners() {
    this.subscriptions.push(
      this.toast.successMessage$.subscribe((msg) => {
        if (msg) {
          if (this.successTimeout) {
            clearTimeout(this.successTimeout);
          }

          this.successMsg = msg;
          this.showSuccess = true;
          this.successTimeout = setTimeout(() => {
            this.showSuccess = false;
          }, 3000);
        }
      }),
    );

    this.subscriptions.push(
      this.toast.clientErrorMessage$.subscribe((msg) => {
        if (msg) {
          if (this.clientErrorTimeout) {
            clearTimeout(this.clientErrorTimeout);
          }

          this.clientErrorMsg = msg;
          this.showClientError = true;
          this.clientErrorTimeout = setTimeout(() => {
            this.showClientError = false;
          }, 5000);
        }
      }),
    );

    this.subscriptions.push(
      this.toast.serverErrorMessage$.subscribe((msg) => {
        if (msg) {
          if (this.serverErrorTimeout) {
            clearTimeout(this.serverErrorTimeout);
          }

          this.serverErrorMsg = msg;
          this.showServerError = true;
          this.serverErrorTimeout = setTimeout(() => {
            this.showServerError = false;
          }, 6000);
        }
      }),
    );
    this.subscriptions.push(
      this.toast.processMessage$.subscribe((msg) => {
        this.processMsg = msg;
        this.showProcessing = !!msg;
      }),
    );
  }

  private handleBrowserRefresh() {
    const perfEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

    if (perfEntries.length && perfEntries[0].type === 'reload') {
      this.onBrowserRefresh();
    }
  }

  onBrowserRefresh(): void {
    if (!this.userDetails) {
      return;
    }

    let appId = Apps.CompanyPortal;
    if (this.userDetails.companyid && this.userDetails.companyid === -1) {
      appId = Apps.AdminPortal;
    }

    this.masterService.userPermissions = [];
    this.masterService.rolesLoaded = false;
    this.masterService.getRolesandPerm(appId);
    this.sharedService.getApps().subscribe();
    this.sharedService.getCompanyApps(this.userDetails.companyid);
  }

  closeSuccessToast() {
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
    }
    this.showSuccess = false;
  }
  closeClientErrorToast() {
    if (this.clientErrorTimeout) {
      clearTimeout(this.clientErrorTimeout);
    }
    this.showClientError = false;
  }
  closeServerErrorToast() {
    if (this.serverErrorTimeout) {
      clearTimeout(this.serverErrorTimeout);
    }
    this.showServerError = false;
  }

  closeProcessingToast() {
    this.toast.process('');
  }

  logout() {
    const sessionId = sessionStorage.getItem('sessionId');
    const userDetails = localStorage.getItem('user');

    let signOut = new Signout();
    signOut.sessionId = sessionId;

    if (userDetails) {
      const userDetailsObj = JSON.parse(userDetails);
      signOut.userId = userDetailsObj.userid;

      this.sharedService.logout(signOut).subscribe({
        next: () => this.performLogoutCleanup(),
        error: () => this.performLogoutCleanup(),
      });
    } else {
      this.performLogoutCleanup();
    }
  }
  private performLogoutCleanup() {
    this.sharedService.performFullSessionCleanup();
    this.router.navigate(['/shared/login']);
  }
}
